import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLiveResource } from "@excellent/ui/api/live";
import { activity } from "@excellent/ui/api/contracts/activity";
import { Bell, Folder, User } from "@excellent/ui/icons";
import {
  AccountMenu,
  type AccountUser,
  type AccountWorkspace,
} from "./account-menu";
import { useVerificationEnvOptional } from "./env-provider";
import { Icon } from "./icons";
import { openVerificationSearch } from "./search-palette";
import type { TerminalAccess } from "./terminal-stage";
import { routeForScope } from "../shell/chrome/route-for-scope";
import type { ShellScopeProject } from "../shell/shell-data";
import "./page-action-bar.css";

type ProjectOption = Pick<ShellScopeProject, "slug" | "name">;

function projectSlugFromPath(pathname: string): string | null {
  const slug = pathname.match(/^\/projects\/([^/?#]+)/)?.[1];
  if (!slug || ["map", "new", "sections"].includes(slug)) return null;
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
}

function projectNameFromSlug(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}

export function WindowProjectSwitcher({
  pathname,
  search,
  projects,
  allProjects,
  defaultSlug,
  onNavigate,
}: {
  pathname: string;
  search: string;
  projects: ShellScopeProject[];
  allProjects: ProjectOption[];
  defaultSlug: string | null;
  onNavigate: (to: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const activeSlug = projectSlugFromPath(pathname);
  const options = useMemo(() => {
    const bySlug = new Map<string, ProjectOption>();
    for (const project of [...projects, ...allProjects]) {
      if (!bySlug.has(project.slug)) {
        bySlug.set(project.slug, { slug: project.slug, name: project.name });
      }
    }
    return [...bySlug.values()];
  }, [allProjects, projects]);
  const activeProject = options.find((project) => project.slug === activeSlug);
  const activeName = activeSlug
    ? activeProject?.name ?? projectNameFromSlug(activeSlug)
    : "All projects";

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function selectProject(slug: string | null) {
    setOpen(false);
    onNavigate(
      routeForScope(
        pathname,
        search,
        slug ? { kind: "project", slug } : { kind: "all" },
      ),
    );
  }

  return (
    <div className="vp-project-switcher" ref={rootRef}>
      <button
        type="button"
        className="vp-project-trigger"
        data-action-id="shell.project-switcher"
        aria-label={`Project: ${activeName}`}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <Folder size={15} />
        <span>{activeName}</span>
        <Icon name="chevron-down" size={13} />
      </button>
      {open ? (
        <div
          id={menuId}
          className="vp-project-menu"
          role="menu"
          aria-label="Select project"
        >
          <div className="vp-project-menu-label">Project</div>
          <button
            type="button"
            role="menuitem"
            className="vp-project-option"
            data-action-id="shell.project-all"
            data-active={activeSlug ? undefined : "true"}
            aria-current={activeSlug ? undefined : "true"}
            onClick={() => selectProject(null)}
          >
            <span className="vp-project-option-icon"><Folder size={15} /></span>
            <span className="vp-project-option-copy">
              <strong>All projects</strong>
              <small>Workspace-wide view</small>
            </span>
            {!activeSlug ? <Icon name="check" size={14} /> : null}
          </button>
          {options.map((project) => {
            const active = project.slug === activeSlug;
            return (
              <button
                key={project.slug}
                type="button"
                role="menuitem"
                className="vp-project-option"
                data-action-id="shell.project-switch"
                data-project-slug={project.slug}
                data-active={active ? "true" : undefined}
                aria-current={active ? "true" : undefined}
                onClick={() => selectProject(project.slug)}
              >
                <span className="vp-project-option-icon"><Folder size={15} /></span>
                <span className="vp-project-option-copy">
                  <strong>{project.name}</strong>
                  <small>{project.slug === defaultSlug ? "Default project" : project.slug}</small>
                </span>
                {active ? <Icon name="check" size={14} /> : null}
              </button>
            );
          })}
          <div className="vp-project-menu-separator" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="vp-project-manage"
            data-action-id="shell.project-manage"
            onClick={() => {
              setOpen(false);
              onNavigate("/projects");
            }}
          >
            <Folder size={14} />
            <span>Manage projects</span>
            <Icon name="chevron-right" size={13} />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function WindowActions({
  pathname,
  user,
  authenticated,
  onNavigate,
  workspaces,
  activeWorkspaceId,
  copilotEnabled,
  terminalAccess,
  onLogout,
}: {
  pathname: string;
  user: AccountUser;
  authenticated: boolean;
  onNavigate: (to: string) => void;
  workspaces: AccountWorkspace[];
  activeWorkspaceId: number | null;
  copilotEnabled: boolean;
  terminalAccess: TerminalAccess;
  onLogout: () => void;
}): ReactNode {
  const verificationEnv = useVerificationEnvOptional();
  const activityFeed = useLiveResource(
    activity.list,
    verificationEnv?.environment === "demo" ? null : {},
    { topic: "activity", refreshInterval: 30_000 },
  );
  const notificationCount = activityFeed.data?.groups.reduce(
    (total, group) => total + group.count,
    0,
  ) ?? 0;

  return (
    <div className="vp-window-actions" aria-label="Application tools">
      <button
        type="button"
        className="vp-window-icon"
        data-action-id="shell.window-search"
        aria-label="Search"
        title="Search (Command K or Control K)"
        onClick={() => openVerificationSearch()}
      >
        <Icon name="search" size={16} />
      </button>
      <button
        type="button"
        className="vp-window-icon vp-window-notifications"
        data-action-id="shell.window-notifications"
        data-active={pathname === "/activity" ? "true" : undefined}
        aria-current={pathname === "/activity" ? "page" : undefined}
        aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} updates` : "Notifications"}
        title="Notifications"
        onClick={() => onNavigate("/activity")}
      >
        <Bell size={16} />
        {notificationCount > 0 ? <span className="vp-window-notification-dot" aria-hidden="true" /> : null}
      </button>
      <div className="vp-window-account" aria-label="Account">
        <AccountMenu
          user={user}
          authenticated={authenticated}
          onNavigate={onNavigate}
          workspaces={workspaces}
          activeWorkspaceId={activeWorkspaceId}
          copilotEnabled={copilotEnabled}
          terminalAccess={terminalAccess}
          onLogout={onLogout}
        />
        <span className="vp-window-person" aria-hidden="true">
          <User size={16} />
        </span>
      </div>
    </div>
  );
}
